import { useDashboards } from "@/hooks/useDashboards";
import { useTenant } from "@/hooks/useTenant";
import { useUserRole } from "@/hooks/useUserRole";
import { ConfirmDialog } from "@simplicity/ui";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";

import { AddEditUserDialog } from "./AddEditUserDialog";
import { CsvUploadDialog } from "./CsvUploadDialog";
import { DomainsSection } from "./DomainsSection";
import { GroupDialog } from "./GroupDialog";
import {
  useAddEditUserDialog,
  useConfirmDialog,
  useGroupDialog,
  useUserManagementData,
} from "./hooks";
import { UserGroupsSection } from "./UserGroupsSection";
import { UsersSection } from "./UsersSection";

/**
 * UserManagement orchestrator. Renders the User Management tab on the Settings page. Re-exported
 * from `apps/portal/src/pages/AdminUsers.tsx` as `UserManagementContent` for back-compat with
 * existing call sites and specs; new code should import `UserManagement` directly from this file.
 */
// eslint-disable-next-line max-lines-per-function -- SIM-1760: top-level admin page that wires together useUserManagementData + 4 sub-dialogs + 3 list sections. Extracting wrapper subcomponents would add indirection without changing what the page actually does.
export function UserManagement() {
  const { isAdmin, isSuperAdmin, isLoading: roleLoading } = useUserRole();
  const { tenantPath } = useTenant();
  const navigate = useNavigate();
  const { data: dashboards = [] } = useDashboards();

  const [error, setError] = useState<string | null>(null);
  const confirm = useConfirmDialog();
  const data = useUserManagementData(confirm, setError);
  const userDialog = useAddEditUserDialog(data.createUser, data.updateUserRole, setError);

  // SIM-2553: one dialog serves both create and rename.
  const groupDialog = useGroupDialog(data);
  const [csvUploadOpen, setCsvUploadOpen] = useState(false);

  useEffect(() => {
    if (!roleLoading && !isAdmin) void navigate(tenantPath("/"));
  }, [isAdmin, roleLoading, navigate, tenantPath]);

  if (roleLoading || data.usersLoading || data.groupsLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground text-sm">Loading...</p>
      </div>
    );
  }

  return (
    <div className="w-full">
      {error && (
        <div className="mb-4 p-3 border border-destructive rounded text-sm text-destructive bg-destructive/10">
          {error}
        </div>
      )}

      <UserGroupsSection
        groups={data.groups}
        dashboards={dashboards}
        users={data.users}
        defaultGroupId={data.defaultGroupId}
        onSetDefaultGroup={data.saveDefaultGroupId}
        onRenameGroup={groupDialog.openRename}
        onDeleteGroup={data.handleDeleteGroup}
        onCreateGroup={groupDialog.openCreate}
        onError={setError}
      />

      <UsersSection
        users={data.users}
        currentUserId={data.user?.id}
        onEditUser={userDialog.openEdit}
        onRemoveUser={data.handleRemoveUser}
        onAddUser={userDialog.openAdd}
        onUploadCsv={() => setCsvUploadOpen(true)}
      />

      {isSuperAdmin && (
        <DomainsSection
          domains={data.domains}
          domainsLoading={data.domainsLoading}
          onAddDomain={(d) => {
            void data.handleAddDomain(d);
          }}
          onRemoveDomain={(id) => {
            void data.handleRemoveDomain(id);
          }}
        />
      )}

      <AddEditUserDialog
        open={userDialog.open}
        onOpenChange={userDialog.setOpen}
        editingUserId={userDialog.editingUserId}
        email={userDialog.email}
        name={userDialog.name}
        role={userDialog.role}
        onSetEmail={userDialog.setEmail}
        onSetName={userDialog.setName}
        onSetRole={userDialog.setRole}
        onConfirm={() => {
          void userDialog.confirm();
        }}
        isPending={userDialog.isPending}
      />

      <GroupDialog
        key={groupDialog.dialogKey}
        open={groupDialog.open}
        mode={groupDialog.mode}
        initialName={groupDialog.initialName}
        onOpenChange={groupDialog.onOpenChange}
        onSubmit={groupDialog.onSubmit}
        isPending={groupDialog.isPending}
      />

      <CsvUploadDialog
        open={csvUploadOpen}
        onOpenChange={setCsvUploadOpen}
        onUpload={data.handleCsvUpload}
      />

      <ConfirmDialog
        open={confirm.open}
        onOpenChange={confirm.setOpen}
        title={confirm.title}
        description={confirm.description}
        onConfirm={() => confirm.action?.()}
        options={{ destructive: true }}
      />
    </div>
  );
}
